import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Image from 'src/components/Image';
import ModalLayoutBase, { ModalContent as ModalContentBase } from 'src/components/ModalLayout';
import ResizingText from 'src/components/ResizingText';
import useHelpCenterUrl from 'src/hooks/useHelpCenterUrl';
import useLogin from 'src/hooks/useLogin';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { isDeviceAgeRestrictedAtom } from 'src/stores/auth/atoms';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import { hoverAndActiveCss } from 'src/utils/styled';
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
  height: 600px;
`;
const ModalContent = styled(ModalContentBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding-bottom: 40px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    gap: 16px;
    padding-bottom: 60px;
  }
`;
const AgeRestrictedIcon = styled(Image)`
  width: 160px;
  height: 160px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 120px;
    height: 120px;
  }
`;
const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  gap: 12px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    gap: 8px;
  }
`;
const Title = styled.h1`
  all: unset;
  font-weight: 700;
  font-size: 40px;
  line-height: 56px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const Description = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content10};
`;
const HelpCenterLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  box-sizing: border-box;
  padding: 0px 16px;
  border-radius: 9999px;
  background-color: ${({
  theme
}) => theme.color.surface4};
  text-decoration: none;
  position: absolute;
  bottom: 24px;

  ${({
  theme
}) => hoverAndActiveCss(`
    background-color: ${theme.color.surface6};
  `)}

  ${({
  theme
}) => theme.screenSize.mobile} {
    bottom: 16px;
  }
`;
const HelpCenterText = styled(ResizingText)`
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  text-align: center;
  color: ${({
  theme
}) => theme.color.content11};
`;
interface Props {
  /**
   * 노출 트리거
   */
  reason: 'registration' | 'live' | 'face_verify';

  /**
   * 모든 이탈 가능 수단을 막을 지 여부 (딤 클릭 기능, 닫기 버튼 제공 등)
   */
  disableEscape?: boolean;
}
const AgeRestrictedModal = ({
  reason,
  disableEscape = false
}: Props) => {
  const [isDeviceAgeRestricted, setIsDeviceAgeRestricted] = useAtom(isDeviceAgeRestrictedAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const helpCenterURL = useHelpCenterUrl();
  const {
    selectedTab
  } = useSelectedTab();
  const {
    t
  } = useTranslation();
  const {
    logout
  } = useLogin();
  const tab = reason === 'face_verify' ? 'onboarding' : selectedTab.name;
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.BLOCK,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_UNDERAGE_BLOCK,
      eventParams: {
        action_category: 'impression',
        tab,
        page: 'main',
        target: 'underage_block',
        reason
      }
    });
  }, [tab, reason]);
  useEffect(() => {
    // 최초로 영구제한 걸렸을 때에만 로그 전송
    if (isDeviceAgeRestricted) return;
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.ANYWHERE__REPORT_UNDERAGE_BLOCK,
      eventParams: {
        action_category: 'report',
        tab,
        page: 'main',
        target: 'underage_block',
        reason
      }
    });
  }, [isDeviceAgeRestricted, tab, reason]);
  useEffect(() => {
    setIsDeviceAgeRestricted(true);
    logout();
  }, [setIsDeviceAgeRestricted, logout]);
  const handleClickHelpCenter = () => {
    pushEvent({
      eventType: EVENT_TYPE.BLOCK,
      eventName: EVENT_NAME.ANYWHERE__CLICK_BLOCKED_CONTACT_US,
      eventParams: {
        action_category: 'click',
        tab,
        page: 'main',
        target: 'contact_us',
        reason
      }
    });
  };
  const navButtonType = disableEscape ? ModalNavButtonType.NONE : ModalNavButtonType.CLOSE;
  return <ModalLayout isDim closeWhenClickOutside={!disableEscape} closeWithESC={!disableEscape} navButtonType={navButtonType} mobileNavButtonType={navButtonType} onClose={() => closeModal(ModalType.AGE_RESTRICTED)} data-sentry-element="ModalLayout" data-sentry-component="AgeRestrictedModal" data-sentry-source-file="index.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <AgeRestrictedIcon src='/images/register/imgNoEntry.png' alt='Restricted' data-sentry-element="AgeRestrictedIcon" data-sentry-source-file="index.tsx" />
        <TextWrap data-sentry-element="TextWrap" data-sentry-source-file="index.tsx">
          <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">{t('SIGNUP__BY_AGE_TITLE')}</Title>
          <Description data-sentry-element="Description" data-sentry-source-file="index.tsx">{t('face_verification_result_ban')}</Description>
        </TextWrap>
        <HelpCenterLink href={helpCenterURL} target='_blank' onClick={handleClickHelpCenter} data-sentry-element="HelpCenterLink" data-sentry-source-file="index.tsx">
          <HelpCenterText minFontSize={12} maxLines={1} data-sentry-element="HelpCenterText" data-sentry-source-file="index.tsx">
            {t('LOGIN__CONTACT_US')}
          </HelpCenterText>
        </HelpCenterLink>
      </ModalContent>
    </ModalLayout>;
};
export default AgeRestrictedModal;